import { sonarSdkConfig, type EnvironmentId } from '@/config/environments';

type SonarSession = { token: string; agentId?: string };

declare global {
  interface Window {
    MelioSonar?: {
      createSession: (publishableKey: string, options?: { businessEntityId?: string }) => Promise<SonarSession>;
    };
  }
}

const INIT_TIMEOUT_MS = 8000;

/**
 * Partner api-keys are `melio_sk_<env>_…` secrets (older ones `melio_secret_<env>_…`). The Sonar SDK is a
 * remotely loaded script that sends its first argument to the Sonar session endpoint, so it must only ever
 * receive a non-secret identifier (the partner name or a `pk_<env>_…` key) — never an api-key.
 */
const SECRET_KEY_RE = /^\s*melio_(?:sk|secret)_/i;

export function isSecretLookingKey(value: string): boolean {
  return SECRET_KEY_RE.test(value);
}

const SESSION_TTL_MS = 10 * 60 * 1000;

type CachedSession = { createdAt: number; promise: Promise<SonarSession | null> };

const scriptPromises = new Map<string, Promise<void>>();
const sessionPromises = new Map<string, CachedSession>();

function loadScript(src: string): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    if (window.MelioSonar) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => reject(new Error(`failed to load ${src}`)));
    document.head.appendChild(el);
  });

  scriptPromises.set(src, promise);
  return promise;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('sonar sdk timed out')), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export function preloadSonarSdk(env: EnvironmentId): Promise<void> {
  const cfg = sonarSdkConfig(env);
  if (!cfg) return Promise.resolve();
  return loadScript(cfg.scriptUrl).catch(() => undefined);
}

/**
 * `publishableKey` is a NON-SECRET identifier (the partner name from `GET /health` or `/me`, or the `pk_<env>_…`
 * key from `GET /keys`). A secret-looking value (`melio_sk_…`) is refused without loading the SDK, so a
 * regression upstream can never transmit an api-key to Sonar; the caller gets `null` (no session), the same
 * as any other session failure.
 */
export function ensureSonarSession(env: EnvironmentId, publishableKey: string, businessEntityId?: string): Promise<SonarSession | null> {
  if (isSecretLookingKey(publishableKey)) {
    console.error('Refusing to create a Sonar session: the value passed as the publishable key looks like a secret api-key.');
    return Promise.resolve(null);
  }
  const cacheKey = `${env}:${publishableKey}:${businessEntityId ?? ''}`;
  const cached = sessionPromises.get(cacheKey);
  if (cached && Date.now() - cached.createdAt < SESSION_TTL_MS) return cached.promise;

  const cfg = sonarSdkConfig(env);
  if (!cfg) return Promise.resolve(null);

  const promise = withTimeout(
    loadScript(cfg.scriptUrl).then(() => {
      if (!window.MelioSonar) throw new Error('MelioSonar global not available');
      return window.MelioSonar.createSession(publishableKey, { businessEntityId });
    }),
    INIT_TIMEOUT_MS
  )
    .then((session) => session ?? null)
    .catch(() => null);

  sessionPromises.set(cacheKey, { createdAt: Date.now(), promise });
  return promise;
}

export async function getSonarSessionToken(
  env: EnvironmentId,
  publishableKey: string,
  businessEntityId?: string
): Promise<string | undefined> {
  const session = await ensureSonarSession(env, publishableKey, businessEntityId);
  return session?.token;
}
